/**
 * Textos editáveis das ferramentas de cuidado (A059).
 *
 * O catálogo de ferramentas mora em código (`care-tool.schema.ts`): forma,
 * campos e cores só mudam com deploy. O TEXTO que abre a ferramenta é da
 * equipe de conteúdo, e ela não pode depender de deploy para escrevê-lo. Este
 * é o pedaço do catálogo que mora no banco — coleção `careToolTexts`, um
 * documento por ferramenta, com a chave dela como id.
 *
 * A abertura tem um texto geral e, quando preciso, um por jornada: a mesma
 * ferramenta serve a jornadas que pedem palavras diferentes. "Medicações e
 * suplementos" vale para todas, e a abertura escrita para quem perdeu o bebê
 * ("depois de uma perda...") não pode aparecer para a gestante.
 *
 * O catálogo é um só para o ecossistema: o texto vale para as pacientes de
 * todas as marcas, como o rótulo e a chamada da ferramenta já valiam. Quem
 * escreve é a marca com a funcionalidade `CARE_TOOL_AUTHORING`.
 *
 * Mora em `@eva/schemas` porque a API valida a gravação e escolhe o texto da
 * paciente com a mesma regra que o painel usa para a prévia.
 */
import { z } from 'zod';
import { CareJourneyEnum, type CareJourney } from './care-item.schema';
import type { CareToolDefinition } from './care-tool.schema';

/** Limite de um texto de abertura. É apresentação, não revista. */
export const ABERTURA_MAX = 1500;
/** Limite da descrição: é o texto do card na lista de ferramentas, duas ou três linhas. */
export const DESCRICAO_MAX = 180;

export const CareToolOpeningSchema = z.object({
  /** Vale para toda jornada que não tem texto próprio. Vazio = sem abertura. */
  general: z.string().max(ABERTURA_MAX).default(''),
  /** Texto próprio de uma jornada. Só as que têm texto aparecem aqui. */
  byJourney: z.record(CareJourneyEnum, z.string().max(ABERTURA_MAX)).default({}),
});
export type CareToolOpening = z.infer<typeof CareToolOpeningSchema>;

export const CareToolTextSchema = z.object({
  /** A chave da ferramenta: um documento por ferramenta. */
  id: z.string(),
  toolKey: z.string(),
  opening: CareToolOpeningSchema.default({ general: '', byJourney: {} }),
  /**
   * A descrição do card na lista de ferramentas, no lugar da do catálogo.
   * Ausente ou vazia, vale a do catálogo.
   */
  description: z.string().max(DESCRICAO_MAX).optional(),
  /** Conta que gravou por último. */
  updatedBy: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type CareToolText = z.infer<typeof CareToolTextSchema>;

const TODAS_AS_JORNADAS = CareJourneyEnum.options;

/**
 * As jornadas que a ferramenta atende. Sem `journeys` ela vale para todas —
 * a mesma leitura de `careToolsForJourney`.
 */
export function jornadasDaFerramenta(tool: Pick<CareToolDefinition, 'journeys'>): CareJourney[] {
  if (tool.journeys.length === 0) return [...TODAS_AS_JORNADAS];
  return TODAS_AS_JORNADAS.filter((j) => tool.journeys.includes(j));
}

/**
 * A abertura que a paciente lê: a da jornada dela, senão a geral, senão nada.
 *
 * Jornada desconhecida ou ausente lê a geral — é o caso de quem ainda não tem
 * a jornada resolvida, e ela não pode ficar sem texto por isso.
 */
export function aberturaDaFerramenta(
  opening: Partial<CareToolOpening> | null | undefined,
  jornada: string | null | undefined
): string | null {
  if (!opening) return null;
  const daJornada = jornada ? (opening.byJourney as Record<string, string> | undefined)?.[jornada] : undefined;
  const texto = (daJornada && daJornada.trim()) || (opening.general && opening.general.trim()) || '';
  return texto || null;
}

/** A ferramenta tem algum texto de abertura, geral ou de jornada? */
export function temAbertura(opening: Partial<CareToolOpening> | null | undefined): boolean {
  if (!opening) return false;
  if (opening.general?.trim()) return true;
  return Object.values(opening.byJourney ?? {}).some((t) => !!t?.trim());
}

/** A descrição que a lista mostra: a gravada, senão a do catálogo. */
export function descricaoDaFerramenta(
  tool: Pick<CareToolDefinition, 'description'>,
  texto: { description?: string | null } | null | undefined
): string {
  return texto?.description?.trim() || tool.description;
}

/** A descrição pronta para gravar: uma linha só, aparada; vazia volta ao catálogo. */
export function normalizarDescricao(entrada: unknown): { descricao: string } | { erro: string } {
  if (entrada === undefined || entrada === null) return { descricao: '' };
  if (typeof entrada !== 'string') return { erro: 'A descrição precisa ser texto.' };
  const descricao = entrada.replace(/\s+/g, ' ').trim();
  if (descricao.length > DESCRICAO_MAX) return { erro: `A descrição passou de ${DESCRICAO_MAX} caracteres.` };
  return { descricao };
}

export interface TextosDaFerramenta {
  opening: CareToolOpening;
  description: string;
}

/** Os textos editáveis de uma ferramenta, validados juntos: abertura e descrição. */
export function normalizarTextos(
  tool: Pick<CareToolDefinition, 'journeys' | 'label'>,
  entrada: unknown
): { textos: TextosDaFerramenta } | { erro: string } {
  const { opening, description } = (entrada && typeof entrada === 'object' ? entrada : {}) as { opening?: unknown; description?: unknown };
  const abertura = normalizarAbertura(tool, opening ?? {});
  if ('erro' in abertura) return abertura;
  const descricao = normalizarDescricao(description);
  if ('erro' in descricao) return descricao;
  return { textos: { opening: abertura.abertura, description: descricao.descricao } };
}

const limpar = (texto: unknown): string => (typeof texto === 'string' ? texto.replace(/\r\n?/g, '\n').trim() : '');

/**
 * A abertura pronta para gravar, ou o motivo da recusa.
 *
 * Texto em branco não é gravado: jornada sem texto some de `byJourney`, e é
 * assim que ela volta a ler a geral. Jornada que a ferramenta não atende é
 * recusada em vez de guardada — ninguém leria aquele texto, e ele ficaria no
 * banco parecendo que vale.
 */
export function normalizarAbertura(
  tool: Pick<CareToolDefinition, 'journeys' | 'label'>,
  entrada: unknown
): { abertura: CareToolOpening } | { erro: string } {
  if (!entrada || typeof entrada !== 'object') return { erro: 'Envie a abertura com o texto geral e os textos por jornada.' };
  const { general, byJourney } = entrada as { general?: unknown; byJourney?: unknown };

  if (general !== undefined && typeof general !== 'string') return { erro: 'O texto geral precisa ser texto.' };
  if (byJourney !== undefined && (byJourney === null || typeof byJourney !== 'object' || Array.isArray(byJourney))) {
    return { erro: 'Os textos por jornada precisam vir como jornada → texto.' };
  }

  const geral = limpar(general);
  if (geral.length > ABERTURA_MAX) return { erro: `O texto geral passou de ${ABERTURA_MAX} caracteres.` };

  const atendidas = jornadasDaFerramenta(tool);
  const porJornada: Partial<Record<CareJourney, string>> = {};
  for (const [jornada, bruto] of Object.entries((byJourney ?? {}) as Record<string, unknown>)) {
    if (bruto !== undefined && bruto !== null && typeof bruto !== 'string') return { erro: `O texto da jornada ${jornada} precisa ser texto.` };
    const lida = CareJourneyEnum.safeParse(jornada);
    if (!lida.success) return { erro: `Jornada desconhecida: ${jornada}.` };
    const texto = limpar(bruto);
    if (!texto) continue;
    if (!atendidas.includes(lida.data)) return { erro: `A ferramenta "${tool.label}" não atende a jornada ${jornada}.` };
    if (texto.length > ABERTURA_MAX) return { erro: `O texto da jornada ${jornada} passou de ${ABERTURA_MAX} caracteres.` };
    porJornada[lida.data] = texto;
  }

  return { abertura: { general: geral, byJourney: porJornada } };
}

const ITEM_DE_LISTA = /^([*\-]|\d+\.)\s+/;

/**
 * A abertura como a tela a desenha: cada linha é um parágrafo.
 *
 * Quem escreve aperta Enter e espera uma linha nova; o Markdown junta linhas
 * vizinhas num parágrafo só, e o título colado em cima do texto virava uma
 * frase com ele. Itens de lista seguidos continuam sendo uma lista.
 *
 * É passo de exibição, não de gravação: o texto fica no banco como foi
 * escrito. O app e a prévia do painel passam por aqui, para a prévia não
 * mostrar uma quebra que a paciente não vê.
 */
export function aberturaEmParagrafos(texto: string): string {
  const saida: string[] = [];
  for (const linha of texto.replace(/\r\n?/g, '\n').split('\n')) {
    const anterior = saida[saida.length - 1];
    const vizinhasComTexto = anterior !== undefined && !!anterior.trim() && !!linha.trim();
    const mesmaLista = vizinhasComTexto && ITEM_DE_LISTA.test(anterior) && ITEM_DE_LISTA.test(linha);
    if (vizinhasComTexto && !mesmaLista) saida.push('');
    saida.push(linha);
  }
  return saida.join('\n');
}
